import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "@/i18n/format";
import { coordinatorErrorMessage, isCoordinatorQuery } from "@/lib/coordinator";
import type { DealPanel } from "@/services/moderators/generated/model";
import {
  useModeratorsCoordinatorDealsDraftFollowUpCreate,
  useModeratorsCoordinatorDealsNextStepsCreate,
  useModeratorsCoordinatorDealsSummarizeCreate,
} from "@/services/moderators/generated/moderators-coordinator/moderators-coordinator";

type Recipient = "buyer" | "supplier";

/**
 * WP2.6 — LLM-backed parts of the deal coordinator panel: cached thread
 * summary, next-step suggestions (moderator-only) and follow-up drafts that
 * go to the approval queue. Shows a "not configured" state without a key.
 */
const DealCoordinatorInsights = ({ panel }: { panel: DealPanel }) => {
  const { t } = useTranslation(["deals", "common"]);
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ predicate: (q) => isCoordinatorQuery(q.queryKey) });
  const summarize = useModeratorsCoordinatorDealsSummarizeCreate({ mutation: { onSuccess: refresh } });
  const nextSteps = useModeratorsCoordinatorDealsNextStepsCreate({ mutation: { onSuccess: refresh } });
  const followUp = useModeratorsCoordinatorDealsDraftFollowUpCreate({ mutation: { onSuccess: refresh } });
  const [recipients, setRecipients] = useState<Recipient[]>(["buyer", "supplier"]);
  const [guidance, setGuidance] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const insight = panel.insight;
  const busy = summarize.isPending || nextSteps.isPending || followUp.isPending;

  if (!panel.configured) {
    return (
      <div className="coord-panel__banner" role="status">
        {t("coordinator.notConfigured")}
      </div>
    );
  }

  const toggle = (r: Recipient) =>
    setRecipients(recipients.includes(r) ? recipients.filter((x) => x !== r) : [...recipients, r]);

  const onError = (fallback: string) => (err: unknown) => {
    setNotice(null);
    setError(coordinatorErrorMessage(err, fallback));
  };

  const draft = () => {
    if (recipients.length === 0) {
      setError(t("coordinator.insights.errors.noRecipient"));
      return;
    }
    followUp.mutate(
      { id: panel.dealId, data: { recipients, instructions: guidance.trim() } },
      {
        onSuccess: (res) => {
          setError(null);
          setGuidance("");
          setNotice(t("coordinator.insights.drafted", { count: res.drafts.length }));
        },
        onError: onError(t("coordinator.insights.errors.draftFailed")),
      },
    );
  };

  return (
    <>
      <div className="coord-panel__section">
        <div className="mod-page__card-header">
          <h3>{t("coordinator.insights.summaryTitle")}</h3>
          <button
            type="button"
            className="mod-page__btn mod-page__btn--ghost"
            disabled={busy}
            onClick={() =>
              summarize.mutate(
                { id: panel.dealId },
                { onSuccess: () => setError(null), onError: onError(t("coordinator.insights.errors.summaryFailed")) },
              )
            }
          >
            {summarize.isPending
              ? t("coordinator.insights.working")
              : insight?.summary
                ? t("coordinator.insights.refreshSummary")
                : t("coordinator.insights.summarize")}
          </button>
        </div>
        {insight?.summary ? (
          <>
            <p className="coord-panel__summary">{insight.summary}</p>
            <p className="mod-page__card-meta">
              {t("coordinator.insights.generated", {
                date: insight.summaryGeneratedAt ? formatDateTime(insight.summaryGeneratedAt) : "—",
                name: insight.summaryGeneratedByName ?? "—",
              })}
            </p>
          </>
        ) : (
          <p className="mod-page__empty">{t("coordinator.insights.noSummary")}</p>
        )}
      </div>

      <div className="coord-panel__section">
        <div className="mod-page__card-header">
          <h3>{t("coordinator.insights.nextStepsTitle")}</h3>
          <button
            type="button"
            className="mod-page__btn mod-page__btn--ghost"
            disabled={busy}
            onClick={() =>
              nextSteps.mutate(
                { id: panel.dealId },
                { onSuccess: () => setError(null), onError: onError(t("coordinator.insights.errors.nextStepsFailed")) },
              )
            }
          >
            {nextSteps.isPending ? t("coordinator.insights.working") : t("coordinator.insights.suggest")}
          </button>
        </div>
        {insight && insight.nextSteps.length > 0 ? (
          <>
            <ol className="coord-panel__list">
              {insight.nextSteps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <p className="mod-page__card-meta">
              {t("coordinator.insights.generated", {
                date: insight.nextStepsGeneratedAt ? formatDateTime(insight.nextStepsGeneratedAt) : "—",
                name: insight.nextStepsGeneratedByName ?? "—",
              })}
            </p>
          </>
        ) : (
          <p className="mod-page__empty">{t("coordinator.insights.noNextSteps")}</p>
        )}
      </div>

      <div className="coord-panel__section">
        <h3>{t("coordinator.insights.followUpTitle")}</h3>
        <div className="coord-draft__actions">
          {(["buyer", "supplier"] as const).map((r) => (
            <label key={r} className="coord-draft__log">
              <input type="checkbox" checked={recipients.includes(r)} onChange={() => toggle(r)} />
              {t(`coordinator.insights.to.${r}`)}
            </label>
          ))}
        </div>
        <label className="mod-modal__label">
          {t("coordinator.insights.guidance")}
          <input
            type="text"
            maxLength={500}
            className="mod-page__input"
            value={guidance}
            onChange={(e) => setGuidance(e.target.value)}
          />
        </label>
        <button type="button" className="mod-page__btn mod-page__btn--primary" disabled={busy} onClick={draft}>
          {followUp.isPending ? t("coordinator.insights.working") : t("coordinator.insights.draftFollowUp")}
        </button>
        <p className="mod-page__card-meta">{t("coordinator.insights.followUpNote")}</p>
      </div>

      {notice ? <p className="mod-page__card-meta">{notice}</p> : null}
      {error ? <p className="mod-modal__error">{error}</p> : null}
      {insight?.model ? (
        <p className="mod-page__card-meta">{t("coordinator.insights.model", { model: insight.model })}</p>
      ) : null}
    </>
  );
};

export default DealCoordinatorInsights;
